import { useState, useEffect, useCallback, useRef } from 'react'
import { CustomizationState } from './types'
import { DEFAULT_STATE, applyCustomization } from './widgetState'

export function useWidget(initialState: Partial<CustomizationState> = {}) {
  const initialStateRef = useRef({
    ...DEFAULT_STATE,
    ...initialState
  })
  const [state, setState] = useState<CustomizationState>(initialStateRef.current)
  const messengerReady = useRef(false)

  useEffect(() => {
    console.log('🔄 useWidget useEffect running')

    // Check if script already exists (from previous mount or hot reload)
    const existingScript = document.getElementById('ze-snippet')
    if (existingScript) {
      console.log('⚠️ Script already exists, removing it first')
      existingScript.remove()
      // Also clear the zE reference
      delete window.zE
      delete window.zEMessenger
    }

    // Step 1: Load script FIRST (like test.html)
    console.log('✓ Creating and appending script')
    const script = document.createElement('script')
    script.id = 'ze-snippet'
    script.src = 'https://static.zdassets.com/ekr/snippet.js?key=e125418a-9466-44d8-9b3f-2ac10e911ea4'
    script.async = false  // Force synchronous loading like test.html
    document.body.appendChild(script)

    // Step 2: Poll for zE availability
    const pollInterval = setInterval(() => {
      if (window.zE) {
        console.log('✓ window.zE detected')
        clearInterval(pollInterval)

        // Step 3: Set config AFTER script loads (matching test.html order!)
        console.log('✓ Setting window.zEMessenger')
        window.zEMessenger = {
          autorender: false
        }

        // Step 4: Render embedded mode
        console.log('✓ Calling render')
        window.zE('messenger', 'render', {
          mode: 'embedded',
          conversationList: {
            targetElement: '#widget_conversations',
          },
          messageLog: {
            targetElement: '#widget_messages',
          },
        })
        console.log('✓ Render called')

        // Step 5: Mark as ready after short delay
        setTimeout(() => {
          console.log('✓ Widget ready, messengerReady set to true')
          messengerReady.current = true
        }, 500)
      }
    }, 50) // Check every 50ms

    return () => {
      console.log('🧹 Cleanup: clearing interval and removing script')
      clearInterval(pollInterval)
      const existing = document.getElementById('ze-snippet')
      if (existing) existing.remove()
    }
  }, []) // Only run once on mount

  const updateState = useCallback((updates: Partial<CustomizationState>) => {
    console.log('updateState called with:', updates)
    console.log('messengerReady:', messengerReady.current)
    setState(prev => {
      const newState = { ...prev, ...updates }
      // Only apply customization if messenger is ready
      if (messengerReady.current) {
        console.log('Calling applyCustomization with newState')
        applyCustomization(newState) // Call immediately, no setTimeout
      } else {
        console.log('Messenger not ready yet, skipping customization')
      }
      return newState
    })
  }, [])

  const updateStylingPreset = useCallback((preset: 'default' | 'minimalistic') => {
    updateState({ stylingPreset: preset })
  }, [updateState])

  return {
    state,
    updateState,
    updateStylingPreset,
    stylingPreset: state.stylingPreset
  }
}
